import React, { useState, useEffect } from 'react';
import api from '@/config/axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, Spinner, Alert, Button } from '../ui';
import DirhamSymbol from '../components/DirhamSymbol';
import { useToastContext } from '../ui/feedback/ToastProvider';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import {
  RefreshCw,
  TrendingUp,
  Activity,
  Clock,
  Calendar,
  Filter,
} from 'lucide-react';

const COLORS = ['#0A7E66', '#014F43', '#1AB98E', '#4DC8A7', '#086552'];

export default function MinuteROIAnalytics() {
  const { user } = useAuth();
  const { showToast } = useToastContext();
  
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [selectedBroker, setSelectedBroker] = useState(null);
  const [investors, setInvestors] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [hours, setHours] = useState(1);
  
  // Chart data
  const [investorChartData, setInvestorChartData] = useState([]);
  const [brokerChartData, setBrokerChartData] = useState([]);
  const [adminChartData, setAdminChartData] = useState([]);
  const [dailyAverageData, setDailyAverageData] = useState(null);
  
  // Statistics
  const [investorStats, setInvestorStats] = useState(null);
  const [brokerStats, setBrokerStats] = useState(null);
  const [adminStats, setAdminStats] = useState(null);

  // Fetch investors and brokers
  useEffect(() => {
    if (user?.role === 'admin') {
      fetchInvestorsAndBrokers();
    }
  }, [user]);

  const fetchInvestorsAndBrokers = async () => {
    try {
      const [investorsRes, brokersRes] = await Promise.all([
        api.get('/users?role=investor&limit=50'),
        api.get('/brokers?limit=50'),
      ]);
      
      setInvestors(investorsRes.data.users || []);
      setBrokers(brokersRes.data.brokers || []);
    } catch (error) {
      console.error('Error fetching investors/brokers:', error);
    }
  };

  // Fetch investor ROI chart data
  const fetchInvestorROIData = async (investorId) => {
    try {
      setLoading(true);
      const response = await api.get(`/minute-roi/chart-data/${investorId}`, {
        params: { hours },
      });
      
      if (response.data.success) {
        const formattedData = response.data.data.map(item => ({
          time: new Date(item.time).toLocaleTimeString(),
          roi: parseFloat(item.roi.toFixed(4)),
          profit: parseFloat(item.profit.toFixed(6)),
          invested: item.invested,
        }));
        
        setInvestorChartData(formattedData);
        
        // Calculate statistics
        if (formattedData.length > 0) {
          const profits = formattedData.map(d => d.profit);
          const rois = formattedData.map(d => d.roi);
          
          setInvestorStats({
            avgROI: (rois.reduce((a, b) => a + b, 0) / rois.length).toFixed(4),
            minROI: Math.min(...rois).toFixed(4),
            maxROI: Math.max(...rois).toFixed(4),
            totalProfit: profits.reduce((a, b) => a + b, 0).toFixed(4),
            avgProfitPerMinute: (profits.reduce((a, b) => a + b, 0) / profits.length).toFixed(6),
            dataPoints: formattedData.length,
          });
        }
      }
    } catch (error) {
      showToast('Error fetching investor ROI data', 'error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch broker commission data
  const fetchBrokerData = async (brokerId) => {
    try {
      setLoading(true);
      
      // Get broker's daily average ROI
      const response = await api.get(`/minute-roi/daily-average-all`, {
        params: { date: selectedDate },
      });
      
      if (response.data.success && response.data.investors.length > 0) {
        const brokerData = response.data.investors.find(
          inv => inv.investor_id === brokerId
        );
        
        if (brokerData) {
          // Format data for chart
          const chartData = [
            {
              name: 'Commission',
              value: parseFloat(brokerData.totalProfit.toFixed(2)),
              roi: parseFloat(brokerData.avgROI.toFixed(4)),
            },
          ];
          
          setBrokerChartData(chartData);
          setBrokerStats({
            avgROI: brokerData.avgROI.toFixed(4),
            minROI: brokerData.minROI.toFixed(4),
            maxROI: brokerData.maxROI.toFixed(4),
            totalCommission: brokerData.totalProfit.toFixed(4),
            dataPoints: brokerData.count,
            avgInvested: brokerData.avgInvestedAmount.toFixed(2),
          });
        }
      }
    } catch (error) {
      showToast('Error fetching broker data', 'error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch admin profit data
  const fetchAdminData = async () => {
    try {
      setLoading(true);
      
      const response = await api.get('/minute-roi/daily-average-all', {
        params: { date: selectedDate },
      });
      
      if (response.data.success && response.data.investors.length > 0) {
        // Format all investors' data for stacked chart
        const chartData = response.data.investors.map(inv => ({
          name: inv.investor_email.split('@')[0],
          profit: parseFloat(inv.totalProfit.toFixed(2)),
          roi: parseFloat(inv.avgROI.toFixed(4)),
        }));
        
        setAdminChartData(chartData);
        setDailyAverageData({
          totalInvestors: response.data.investorCount,
          overallAvgROI: response.data.overallAvgROI.toFixed(4),
          totalProfit: response.data.totalProfit.toFixed(2),
          avgProfitPerInvestor: (response.data.totalProfit / response.data.investorCount).toFixed(2),
        });
        
        // Calculate admin stats (50% split between Admin A and B)
        const adminProfit = response.data.totalProfit / 2;
        setAdminStats({
          adminAProfit: adminProfit.toFixed(2),
          adminBProfit: adminProfit.toFixed(2),
          totalProfit: response.data.totalProfit.toFixed(2),
          overallROI: response.data.overallAvgROI.toFixed(4),
          investorCount: response.data.investorCount,
        });
      }
    } catch (error) {
      showToast('Error fetching admin data', 'error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await api.post('/minute-roi/calculate');
      showToast('ROI calculation triggered', 'success');
      
      if (selectedInvestor) {
        await fetchInvestorROIData(selectedInvestor._id);
      }
      if (selectedBroker) {
        await fetchBrokerData(selectedBroker._id);
      }
      if (user?.role === 'admin') {
        await fetchAdminData();
      }
    } catch (error) {
      showToast('Error triggering ROI calculation', 'error');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Real-time ROI Analytics</h1>
          <p className="text-gray-600 mt-2">Minute-by-minute profit tracking and analysis</p>
        </div>
        <Button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Calculating...' : 'Refresh Data'}
        </Button>
      </div>

      {/* Controls */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-2" />
              Date
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Clock className="w-4 h-4 inline mr-2" />
              Hours
            </label>
            <select
              value={hours}
              onChange={(e) => setHours(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            >
              <option value={1}>Last 1 Hour</option>
              <option value={6}>Last 6 Hours</option>
              <option value={12}>Last 12 Hours</option>
              <option value={24}>Last 24 Hours</option>
            </select>
          </div>

          {user?.role === 'admin' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Filter className="w-4 h-4 inline mr-2" />
                  Investor
                </label>
                <select
                  value={selectedInvestor?._id || ''}
                  onChange={(e) => {
                    const investor = investors.find(i => i._id === e.target.value);
                    setSelectedInvestor(investor);
                    if (investor) {
                      fetchInvestorROIData(investor._id);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Select Investor</option>
                  {investors.map(inv => (
                    <option key={inv._id} value={inv._id}>
                      {inv.firstName} {inv.lastName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Filter className="w-4 h-4 inline mr-2" />
                  Broker
                </label>
                <select
                  value={selectedBroker?._id || ''}
                  onChange={(e) => {
                    const broker = brokers.find(b => b._id === e.target.value);
                    setSelectedBroker(broker);
                    if (broker) {
                      fetchBrokerData(broker._id);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Select Broker</option>
                  {brokers.map(broker => (
                    <option key={broker._id} value={broker._id}>
                      {broker.name}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>
      </Card>

      {/* Investor ROI Chart */}
      {selectedInvestor && (
        <div className="space-y-4">
          <Card className="p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-800">
                  {selectedInvestor.firstName} {selectedInvestor.lastName} - ROI Tracking
                </h2>
                <p className="text-gray-600">{selectedInvestor.email}</p>
              </div>
              <Activity className="w-8 h-8 text-emerald-600" />
            </div>

            {loading ? (
              <Spinner />
            ) : investorChartData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={investorChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" />
                    <YAxis yAxisId="left" label={{ value: 'ROI %', angle: -90, position: 'insideLeft' }} />
                    <YAxis yAxisId="right" orientation="right" label={{ value: 'Profit $', angle: 90, position: 'insideRight' }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '8px' }}
                      formatter={(value) => value.toFixed(6)}
                    />
                    <Legend />
                    <Line yAxisId="left" type="monotone" dataKey="roi" stroke={COLORS[0]} dot={false} name="ROI %" />
                    <Line yAxisId="right" type="monotone" dataKey="profit" stroke={COLORS[2]} dot={false} name="Profit per Min ($)" />
                  </LineChart>
                </ResponsiveContainer>

                {/* Statistics */}
                {investorStats && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Avg ROI</p>
                      <p className="text-2xl font-bold text-emerald-600">{investorStats.avgROI}%</p>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">ROI Range</p>
                      <p className="text-sm font-bold text-emerald-600">
                        {investorStats.minROI}% - {investorStats.maxROI}%
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Total Profit</p>
                      <p className="text-2xl font-bold text-emerald-600">
                        <DirhamSymbol /> {investorStats.totalProfit}
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Avg/Min</p>
                      <p className="text-sm font-bold text-emerald-600">
                        <DirhamSymbol /> {investorStats.avgProfitPerMinute}
                      </p>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <Alert type="info">No ROI data available yet</Alert>
            )}
          </Card>
        </div>
      )}

      {/* Broker Commission Chart */}
      {selectedBroker && (
        <Card className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-800">{selectedBroker.name} - Commission Analysis</h2>
              <p className="text-gray-600">{selectedBroker.email}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-emerald-600" />
          </div>

          {loading ? (
            <Spinner />
          ) : brokerChartData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={brokerChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis label={{ value: 'Commission (AED)', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Bar dataKey="value" fill={COLORS[1]} name="Commission" />
                </BarChart>
              </ResponsiveContainer>

              {brokerStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Total Commission</p>
                    <p className="text-2xl font-bold text-blue-600">
                      <DirhamSymbol /> {brokerStats.totalCommission}
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Avg ROI</p>
                    <p className="text-2xl font-bold text-blue-600">{brokerStats.avgROI}%</p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">ROI Range</p>
                    <p className="text-sm font-bold text-blue-600">
                      {brokerStats.minROI}% - {brokerStats.maxROI}%
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Data Points</p>
                    <p className="text-2xl font-bold text-blue-600">{brokerStats.dataPoints}</p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <Alert type="info">No commission data available</Alert>
          )}
        </Card>
      )}

      {/* Admin Profit Overview */}
      {user?.role === 'admin' && (
        <Card className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-800">Daily Profit Distribution</h2>
              <p className="text-gray-600">All Investors on {selectedDate}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-emerald-600" />
          </div>

          {loading ? (
            <Spinner />
          ) : adminChartData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={400}>
                <AreaChart data={adminChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis label={{ value: 'Profit (AED)', angle: -90, position: 'insideLeft' }} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '8px' }}
                    formatter={(value) => `AED ${value.toFixed(2)}`}
                  />
                  <Area type="monotone" dataKey="profit" fill={COLORS[0]} stroke={COLORS[0]} />
                </AreaChart>
              </ResponsiveContainer>

              {dailyAverageData && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Overall Avg ROI</p>
                    <p className="text-2xl font-bold text-purple-600">{dailyAverageData.overallAvgROI}%</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Total Profit</p>
                    <p className="text-2xl font-bold text-purple-600">
                      <DirhamSymbol /> {dailyAverageData.totalProfit}
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Avg/Investor</p>
                    <p className="text-2xl font-bold text-purple-600">
                      <DirhamSymbol /> {dailyAverageData.avgProfitPerInvestor}
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Active Investors</p>
                    <p className="text-2xl font-bold text-purple-600">{dailyAverageData.totalInvestors}</p>
                  </div>
                </div>
              )}

              {adminStats && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                  <div className="bg-gradient-to-br from-orange-50 to-yellow-50 p-6 rounded-lg border-2 border-orange-200">
                    <p className="text-sm text-gray-600 mb-2">Admin A Profit (50%)</p>
                    <p className="text-3xl font-bold text-orange-600">
                      <DirhamSymbol /> {adminStats.adminAProfit}
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-lime-50 p-6 rounded-lg border-2 border-green-200">
                    <p className="text-sm text-gray-600 mb-2">Admin B Profit (50%)</p>
                    <p className="text-3xl font-bold text-green-600">
                      <DirhamSymbol /> {adminStats.adminBProfit}
                    </p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <Alert type="info">No profit data available for this date</Alert>
          )}
        </Card>
      )}
    </div>
  );
}
